import { useEffect, useState } from 'react';

const PADDING = 10;

export function TourSpotlight({ target }: { target?: string }) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(`[data-tour="${target}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      setRect(el.getBoundingClientRect());
    };

    update();
    const timer = window.setTimeout(update, 350);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [target]);

  if (!target || !rect) {
    return (
      <div
        className="fixed inset-0 bg-yhelp-black/70 motion-reduce:transition-none"
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      className="pointer-events-none fixed inset-0 motion-reduce:transition-none"
      aria-hidden="true"
    >
      <div
        className="fixed rounded-xl ring-4 ring-yhelp-yellow motion-reduce:transition-none"
        style={{
          top: rect.top - PADDING,
          left: rect.left - PADDING,
          width: rect.width + PADDING * 2,
          height: rect.height + PADDING * 2,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.72)',
        }}
      />
    </div>
  );
}

export interface TooltipPosition {
  top: number;
  left: number;
  transform: string;
}

export function tooltipPositionForTarget(
  target: string | undefined,
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center',
): TooltipPosition {
  if (!target || placement === 'center') {
    return {
      top: window.innerHeight / 2,
      left: window.innerWidth / 2,
      transform: 'translate(-50%, -50%)',
    };
  }

  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) {
    return {
      top: window.innerHeight - 120,
      left: window.innerWidth / 2,
      transform: 'translate(-50%, 0)',
    };
  }

  const rect = el.getBoundingClientRect();
  const cardWidth = Math.min(384, window.innerWidth - 32);
  const cardHeight = 220;
  const gap = 16;

  switch (placement) {
    case 'top':
      return {
        top: Math.max(16, rect.top - gap),
        left: Math.min(
          window.innerWidth - cardWidth / 2 - 16,
          Math.max(cardWidth / 2 + 16, rect.left + rect.width / 2),
        ),
        transform: 'translate(-50%, -100%)',
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: Math.max(16, rect.left - gap),
        transform: 'translate(-100%, -50%)',
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: Math.min(window.innerWidth - 16, rect.right + gap),
        transform: 'translate(0, -50%)',
      };
    case 'bottom':
    default:
      return {
        top: Math.min(window.innerHeight - cardHeight - 16, rect.bottom + gap),
        left: Math.min(
          window.innerWidth - cardWidth / 2 - 16,
          Math.max(cardWidth / 2 + 16, rect.left + rect.width / 2),
        ),
        transform: 'translate(-50%, 0)',
      };
  }
}
